import axios from 'axios';
import config from '../config';
import { store } from '../store/store';
import { setUser, clearUser } from '../features/user/userSlice';

const axiosInstance = axios.create({
  baseURL: config.baseURL,
});

// ---------------------------------------------------------------------------
// Request interceptor — attach the current access token to every request
// ---------------------------------------------------------------------------
axiosInstance.interceptors.request.use(
  (cfg) => {
    const { token } = store.getState().user;
    if (token) {
      cfg.headers.Authorization = `Bearer ${token}`;
    }
    return cfg;
  },
  (err) => Promise.reject(err)
);

// ---------------------------------------------------------------------------
// Response interceptor — on 401, silently refresh once and retry
//
// IMPORTANT: the server ROTATES the refresh token on every successful
// refresh (see authController.refresh). So after we call /api/auth/refresh
// we MUST persist the NEW refreshToken that comes back, not the old one.
// Otherwise the next refresh will fail with "Refresh token no longer valid".
// ---------------------------------------------------------------------------
let refreshPromise = null;

async function refreshAccessToken() {
  const { refreshToken } = store.getState().user;
  if (!refreshToken) throw new Error('No refresh token available');

  const res = await axios.post(
    `${config.baseURL}/api/auth/refresh`,
    { refreshToken },
    { headers: { 'Content-Type': 'application/json' } }
  );

  const newAccessToken = res.data?.token || res.data?.accessToken;
  const newRefreshToken = res.data?.refreshToken;

  if (!newAccessToken || !newRefreshToken) {
    throw new Error('Refresh response missing token(s)');
  }

  // Persist BOTH tokens — the server rotated the refresh token, the old
  // one is now invalid on the backend (its hash no longer matches).
  const { userInfo } = store.getState().user;
  store.dispatch(
    setUser({
      userInfo,
      token: newAccessToken,
      refreshToken: newRefreshToken,
    })
  );

  // keep localStorage in sync for components that read it directly
  localStorage.setItem('token', newAccessToken);
  localStorage.setItem('refreshToken', newRefreshToken);

  return newAccessToken;
}

axiosInstance.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    // Only intercept 401s, and only once per request
    if (status !== 401 || !original || original._retried) {
      return Promise.reject(error);
    }

    const { refreshToken } = store.getState().user;
    if (!refreshToken) {
      // No way to recover — force logout
      store.dispatch(clearUser());
      window.location.href = '/login';
      return Promise.reject(error);
    }

    original._retried = true;

    try {
      // Collapse concurrent 401s into a single refresh call
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
      }
      const newAccessToken = await refreshPromise;

      original.headers = original.headers || {};
      original.headers.Authorization = `Bearer ${newAccessToken}`;
      return axiosInstance(original);
    } catch (refreshErr) {
      // Refresh failed → refresh token is expired or revoked → real logout
      store.dispatch(clearUser());
      window.location.href = '/login';
      return Promise.reject(refreshErr);
    }
  }
);

export default axiosInstance;